/* used for both AMC and Hospital reserach type sites:
- Academic Medical Center or Clinical Trials Unit
- Hospital or Health System
*/

import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getControlId } from '@veeva-ctv/utils';
import { FormikProps } from 'formik';
import {
  GeneralSearchQuery,
  GeneralSearchQueryVariables,
  SearchType,
  SiteApplicationInput,
} from '@/gql/graphql';
import StandardCountryStateProvinceInput from '@/components/StandardCountryStateProvinceInput';
import { UIOnlyFields } from '@/types';
import { AddOutlined, SearchRounded } from '@mui/icons-material';
import { colors } from '@veeva-ctv/component-lib';
import { useLazyQuery } from '@apollo/client';
import { GENERAL_SEARCH } from '@/queries';
import { useStyles as useStylesAutocomplete } from '@/hooks/styles/autocomplete';
import { debounce } from '@/utils';
import { useCallback } from 'react';


const ADD_NEW = ' (Add a new institution)';

interface AMCFormProps {
  formik: FormikProps<UIOnlyFields & SiteApplicationInput>;
}

const AMCHospitalForm = ({ formik }: AMCFormProps) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const classesAutocomplete = useStylesAutocomplete();

  const [searchOrgName, { data: searchOrgNameData, loading: searchOrgNameLoading }] = useLazyQuery<
    GeneralSearchQuery,
    GeneralSearchQueryVariables
  >(GENERAL_SEARCH);

  const doOrgNameSearch = useCallback(
    debounce((value: string) => {
      void searchOrgName({
        variables: {
          input: {
            generalSearchText: value.replace(/[\n\t]/g, '').trim(),
            filterText: '',
            generalSearchType: SearchType.Employer,
          },
          size: 10,
        },
      });
    }, 300),
    []
  );

  const parseEmptyStateValue = (value: string) => value.replace(ADD_NEW, '');

  const calculateOptions = () => {
    if (formik.values.organization) {
      if (
        searchOrgNameData &&
        searchOrgNameData.generalSearch &&
        searchOrgNameData.generalSearch.payload &&
        searchOrgNameData.generalSearch.payload.length > 0
      ) {
        return [
          ...searchOrgNameData.generalSearch.payload,
          `${formik.values.organization}${ADD_NEW}`,
        ];
      }
      return ['NO RESULT', `${formik.values.organization}${ADD_NEW}`];
    }
    return [];
  };

  return (
    <>
      <Grid item xs={12}>
        <Box mt={1}>
          <Autocomplete
            autoComplete
            id={getControlId({ type: 'text', name: 'organizationName' })}
            loading={searchOrgNameLoading}
            inputValue={formik.values.organization as string}
            freeSolo
            onInputChange={(_e, value, reason) => {
              if (value === '' || reason === 'clear') {
                void formik.setFieldValue('organization', '');
                doOrgNameSearch('');
                return;
              }
              doOrgNameSearch(value);
              void formik.setFieldValue('organization', parseEmptyStateValue(value));
            }}
            onChange={(_e, value) => {
              void formik.setFieldValue('organization', value ? parseEmptyStateValue(value) : '');
            }}
            options={calculateOptions()}
            renderOption={(props, option: string) => {
              if (option.endsWith(ADD_NEW)) {
                if (searchOrgNameLoading) {
                  return null;
                }
                return (
                  <li
                    {...props}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AddOutlined />
                    <b>{parseEmptyStateValue(option)}</b>
                    &nbsp;{ADD_NEW}
                  </li>
                );
              }
              if (option === 'NO RESULT') {
                if (searchOrgNameLoading) {
                  return null;
                }
                return (
                  <li
                    {...props}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      height: '70px',
                    }}>
                    No results
                  </li>
                );
              }
              return <li {...props}>{option}</li>;
            }}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            classes={{
              option: classesAutocomplete.optionSearch,
              listbox: classesAutocomplete.listbox,
            }}
            popupIcon={<SearchRounded />}
            renderInput={(params) => (
              <TextField
                {...params}
                name="organization"
                margin="dense"
                label="Institution name*"
                onBlur={formik.handleBlur}
                error={formik.touched.organization && Boolean(formik.errors.organization)}
                helperText={formik.touched.organization && formik.errors.organization}
                inputProps={{
                  ...params.inputProps,
                }}
                InputProps={{
                  ...params.InputProps,
                  autoComplete: 'one-time-code', // disable autocomplete and autofill
                  endAdornment: (
                    <>
                      {searchOrgNameLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        !formik.values.organization && (
                          <SearchRounded
                            sx={(theme) => ({
                              color: colors.grey[800],
                              marginRight: theme.spacing(0.5),
                              marginLeft: theme.spacing(0.5),
                            })}
                          />
                        )
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'name' })}
          name="name"
          label="What is your specific department, location, or division of research called?*"
          placeholder="Provide the full unabbreviated name. This is required to create your account."
          fullWidth
          inputProps={{ autoComplete: 'one-time-code' }}
          value={formik.values.name}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          //fix for long labels to wrap around nicely on mobile and not overlap with the input
          InputLabelProps={{
            sx: { whiteSpace: smDown ? 'normal' : 'wrap', top: smDown ? '-28px' : '-14px' },
          }}
          sx={{ marginTop: smDown ? '32px' : '20px' }}
        />
      </Grid>

      <StandardCountryStateProvinceInput
        formik={formik}
        addressFieldName="address1"
        stateFieldName="stateProvince"
        countryFieldName="country"
        cityFieldName="city"
        postalCodeFieldName="postalCode"
      />

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'url' })}
          name="url"
          label="Website*"
          fullWidth
          inputProps={{ autoComplete: 'one-time-code' }}
          value={formik.values.url}
          error={formik.touched.url && Boolean(formik.errors.url)}
          helperText={formik.touched.url && formik.errors.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>
    </>
  );
};

export default AMCHospitalForm;
